import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const NotificationContext = createContext(null);

// Simple beep synthesized with WebAudio — no external assets
function playBeep(frequency = 720, duration = 120, volume = 0.06) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = frequency;
    gain.gain.value = volume;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    setTimeout(() => { osc.stop(); ctx.close(); }, duration);
  } catch {}
}

const SOUND_MAP = {
  new_ticket: () => playBeep(760, 120),
  ticket_reply: () => { playBeep(620, 90); setTimeout(() => playBeep(820, 90), 100); },
  ticket_assigned: () => playBeep(540, 160),
  test: () => playBeep(880, 140),
  default: () => playBeep(680, 100),
};

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nsound") ?? "true"); } catch { return true; }
  });
  const seenIds = useRef(new Set());
  const firstLoad = useRef(true);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      if (!firstLoad.current && soundEnabled) {
        const newOnes = data.items.filter((n) => !seenIds.current.has(n.notification_id));
        newOnes.forEach((n) => (SOUND_MAP[n.kind] || SOUND_MAP.default)());
      }
      data.items.forEach((n) => seenIds.current.add(n.notification_id));
      setItems(data.items);
      setUnread(data.unread);
      firstLoad.current = false;
    } catch {}
  }, [user, soundEnabled]);

  useEffect(() => {
    if (!user) return;
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [user, load]);

  const markRead = async (nid) => {
    await api.post(`/notifications/${nid}/read`);
    setItems((prev) => prev.map((n) => (n.notification_id === nid ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
  };

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const setSound = (v) => {
    setSoundEnabled(v);
    localStorage.setItem("nsound", JSON.stringify(v));
  };

  return (
    <NotificationContext.Provider value={{ items, unread, load, markRead, markAllRead, soundEnabled, setSound, playSample: SOUND_MAP.default }}>
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => useContext(NotificationContext);
